import type {
  AddressCountryCode,
  AddressReference,
  AddressReferenceOption,
} from "../types/address-reference";

const ADDRESS_COUNTRY_CODES = new Set<AddressCountryCode>(["US", "MX"]);

export const DEFAULT_ADDRESS_COUNTRY: AddressCountryCode = "US";

export function isAddressCountryCode(value: string | undefined | null): value is AddressCountryCode {
  return value === "US" || value === "MX";
}

export function coerceAddressCountryCode(
  value: string | undefined | null,
): AddressCountryCode {
  return isAddressCountryCode(value) ? value : DEFAULT_ADDRESS_COUNTRY;
}

function normalizeOptions(raw: unknown): AddressReferenceOption[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const row = entry as Record<string, unknown>;
      const code = typeof row.code === "string" ? row.code.trim() : "";
      const label = typeof row.label === "string" ? row.label.trim() : "";
      if (!code || !label) return null;
      return {code, label};
    })
    .filter((option): option is AddressReferenceOption => option != null);
}

function normalizeSubdivisionMap(raw: unknown): AddressReference["subdivisions"] {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {US: [], MX: []};
  }

  const source = raw as Record<string, unknown>;
  return {
    US: normalizeOptions(source.US),
    MX: normalizeOptions(source.MX),
  };
}

export function normalizeAddressReference(raw: unknown): AddressReference {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {countries: [], subdivisions: {US: [], MX: []}};
  }

  const data = raw as Record<string, unknown>;
  const countries = normalizeOptions(data.countries).filter((country) =>
    ADDRESS_COUNTRY_CODES.has(country.code as AddressCountryCode),
  );

  return {
    countries,
    subdivisions: normalizeSubdivisionMap(data.subdivisions),
  };
}

export function getSubdivisionsForCountry(
  reference: AddressReference | undefined,
  country: AddressCountryCode,
): AddressReferenceOption[] {
  return reference?.subdivisions?.[country] ?? [];
}

export function findSubdivisionOption(
  reference: AddressReference | undefined,
  country: AddressCountryCode,
  stateCode: string | undefined | null,
): AddressReferenceOption | null {
  const code = stateCode?.trim();
  if (!code || !reference) return null;
  return getSubdivisionsForCountry(reference, country).find((option) => option.code === code) ?? null;
}

/** Map API state code or full label to subdivision code for form/combobox value. */
export function resolveSubdivisionCode(
  reference: AddressReference | undefined,
  country: AddressCountryCode,
  state: string | undefined | null,
): string {
  const raw = state?.trim() ?? "";
  if (!raw) return "";

  const subdivisions = getSubdivisionsForCountry(reference, country);
  const byCode = subdivisions.find((option) => option.code === raw);
  if (byCode) return byCode.code;

  const lower = raw.toLowerCase();
  const byLabel = subdivisions.find((option) => option.label.toLowerCase() === lower);
  return byLabel?.code ?? raw;
}

export function getPostalCodeLabel(country: AddressCountryCode): string {
  return country === "MX" ? "Postal code" : "ZIP Code";
}

/** Restrict postal input while typing (MX: 5 digits; US: ##### or #####-####). */
export function sanitizePostalCodeInput(
  value: string,
  country: AddressCountryCode,
): string {
  if (country === "MX") {
    return value.replace(/\D/g, "").slice(0, 5);
  }

  const digits = value.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 5) {
    return digits;
  }

  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

export function getPostalCodeInputMode(country: AddressCountryCode): "numeric" | "text" {
  return "numeric";
}

export function getPostalCodeMaxLength(country: AddressCountryCode): number {
  return country === "MX" ? 5 : 10;
}

export function getSubdivisionFieldLabel(country: AddressCountryCode): string {
  return country === "MX" ? "State / Province" : "State";
}

export function formatAddressCountryLabel(
  country: string | undefined | null,
  reference?: AddressReference,
): string {
  const code = coerceAddressCountryCode(country);
  const match = reference?.countries.find((option) => option.code === code);
  return match?.label ?? (code === "MX" ? "Mexico" : "United States");
}

export function formatSubdivisionLabel(
  country: AddressCountryCode,
  stateCode: string | undefined | null,
  reference?: AddressReference,
): string {
  const code = stateCode?.trim();
  if (!code) return "—";
  const match = findSubdivisionOption(reference, country, code);
  return match?.label ?? code;
}

export function validatePostalCodeForCountry(
  zipcode: string | undefined,
  country: AddressCountryCode,
): string | null {
  const value = zipcode?.trim() ?? "";
  if (!value) return "Postal code is required";

  if (country === "MX") {
    if (!/^\d{5}$/.test(value)) {
      return "Mexican postal codes must be exactly 5 digits";
    }
    return null;
  }

  if (!/^\d{5}(-\d{4})?$/.test(value)) {
    return "Enter a valid ZIP code (12345 or 12345-6789)";
  }

  return null;
}

export function validateStateForCountry(
  state: string | undefined,
  country: AddressCountryCode,
  reference?: AddressReference,
): string | null {
  const value = state?.trim() ?? "";
  if (!value) return `${getSubdivisionFieldLabel(country)} is required`;

  if (!reference) return null;

  const subdivisions = getSubdivisionsForCountry(reference, country);
  if (subdivisions.length === 0) return null;

  const isValid = subdivisions.some((option) => option.code === value);
  return isValid ? null : `Invalid ${getSubdivisionFieldLabel(country).toLowerCase()}`;
}
